import SherlockConstant, {
  initialOwnerPropertyListState,
} from "@/mixins/sherlock/const/sherlockConstant.js";
import SherlockAcConstant from "@/mixins/sherlock/const/sherlockAcConstant.js";
import {
  callGetApi,
  callPostApi,
  callPatchApi,
  callDeleteApi,
} from "@/utils/apiWrapper";

export const state = {
  _ownerPropertyList: [],
  _ownerPropertyListLoaded: false,
  _ownerPropertyDetail: null,
  _addressSearchList: [],
};

export const mutations = {
  [SherlockConstant.SET_SHERLOCK_ESTATE_OWNER_PROPERTY_LIST](
    state,
    { initialOwnerPropertyListStates, mode }
  ) {
    state._ownerPropertyListLoaded = true;
    if (mode === "paging") {
      state._ownerPropertyList =
        initialOwnerPropertyListStates?.slice(0, 5) || [];
    } else {
      state._ownerPropertyList = initialOwnerPropertyListStates || [];
    }
  },
  [SherlockConstant.SET_SHERLOCK_ESTATE_OWNER_PROPERTY_DETAIL](
    state,
    initialOwnerPropertyDetailState
  ) {
    state._ownerPropertyDetail = initialOwnerPropertyDetailState || null;
  },
  [SherlockConstant.SET_SHERLOCK_ESTATE_ADDRESS_SEARCH_LIST](
    state,
    initialAddressSearchListStates
  ) {
    state._addressSearchList = initialAddressSearchListStates || [];
  },
};

export const actions = {
  /**************************************************************
   * 다주택 조회 중복 조회 여부 확인
   * owner_name, // 소유자 이름.
   * owner_regno_front, // 주민등록번호 앞자리.
   * owner_regno_back, // 주민등록번호 뒷자리 첫번째.
   **************************************************************/
  async acPostSherlockCheckDuplicateOwnerProperties(
    { commit },
    { owner_name, owner_regno_front, owner_regno_back }
  ) {
    const res = await callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_CHECK_DUPLICATE_OWNER_PROPERTIES}`,
      data: {
        owner_name,
        owner_regno_front,
        owner_regno_back,
      },
    });
    return res;
  },

  /**************************************************************
   * 다주택 조회 포인트 사용 처리
   * owner_name, // 다주택 조회할 소유자 이름.
   * owner_regno_front, // 다주택 조회할 소유자 주민등록번호 앞자리.
   * owner_regno_back, // 다주택 조회할 소유자 주민등록번호 뒷자리 첫번째.
   **************************************************************/
  async acPostSherlockProcessOwnerProperties(
    { commit },
    { owner_name, owner_regno_front, owner_regno_back }
  ) {
    const res = await callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_PROCESS_OWNER_PROPERTIES}`,
      data: {
        owner_name,
        owner_regno_front,
        owner_regno_back,
      },
    });
    // window.console.log("[acPostSherlockProcessOwnerProperties] res: ", res);
    return res;
  },

  /**************************************************************
   * 다주택 조회 상세
   * owner_property_query_seq, // 조회상세 고유번호
   **************************************************************/
  async acGetSherlockEstateOwnerPropertyDetail(
    { commit },
    { owner_property_query_seq }
  ) {
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_ESTATE_OWNER_PROPERTY_DETAIL}`,
      params: {
        owner_property_query_seq,
      },
    });
    // window.console.log("[acGetSherlockEstateOwnerPropertyDetail] res: ", res);
    commit(SherlockConstant.SET_SHERLOCK_ESTATE_OWNER_PROPERTY_DETAIL, res);
    return res;
  },

  /**************************************************************
   * NZSC-2833: 유저가 직접 입력한 추정시세 저장
   * owner_property_result_seq, // 다주택 조회 결과 고유번호
   * price, // 유저 입력 시세 (원). 0 이하 전송 시 삭제
   *
   * 저장 후 상세를 재조회해서 _ownerPropertyDetail을 갱신하므로
   * 호출부는 이 액션만 await하면 충분.
   **************************************************************/
  async acPostSherlockOwnerPropertyUserPrice(
    { dispatch },
    { owner_property_result_seq, price, owner_property_query_seq }
  ) {
    const res = await callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_OWNER_PROPERTY_USER_PRICE}`,
      data: {
        owner_property_result_seq,
        price,
      },
    });
    if (owner_property_query_seq != null) {
      await dispatch("acGetSherlockEstateOwnerPropertyDetail", {
        owner_property_query_seq,
      });
    }
    return res;
  },

  /**************************************************************
   * 유저가 지금까지 포인트 써서 조회한 다주택 조회 목록
   **************************************************************/
  async acGetSherlockEstateOwnerPropertyList(
    { commit },
    { mode = "all", page, pageSize, successOnly = false }
  ) {
    const res = await callGetApi({
      url: SherlockAcConstant.GET_SHERLOCK_ESTATE_OWNER_PROPERTY_LIST,
      params: {
        page,
        pageSize,
        ...(successOnly ? { success_only: "true" } : {}),
      },
    });
    commit(SherlockConstant.SET_SHERLOCK_ESTATE_OWNER_PROPERTY_LIST, {
      initialOwnerPropertyListStates: res || [],
      mode,
    });
    // window.console.log("[acGetSherlockEstateOwnerPropertyList] res: ", res);
    return res;
  },

  /**************************************************************
   * 유저가 지금까지 포인트 써서 조회한 다주택 조회 목록
   **************************************************************/
  async acSearchSherlockEstateOwnerPropertyList(
    { commit },
    { search_keyword }
  ) {
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_ESTATE_OWNER_PROPERTY_LIST_SEARCH}?search_keyword=${search_keyword}`,
      params: {},
    });
    // window.console.log("[acSearchSherlockEstateOwnerPropertyList] res: ", res);
    return res;
  },

  /**************************************************************
   * 주소지 조회 포인트 사용
   * unique_no, // 주소지 고유번호
   **************************************************************/
  async acPostSherlockDealProcessAddressSearch({ commit }, { unique_no }) {
    const res = await callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_DEAL_PROCESS_ADDRESS_SEARCH}`,
      data: { unique_no },
    });
    // window.console.log("[acPostSherlockDealProcessAddressSearch] res: ", res);
    return res;
  },

  /**************************************************************
   * 주소지 조회 후 현재 유효한 (아직 다주택 조회 신청 안된) 목록 조회
   **************************************************************/
  async acGetSherlockEstateAddressSearchList({ commit }) {
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_ESTATE_ADDRESS_SEARCH_LIST}`,
      params: {},
    });
    // window.console.log("[acGetSherlockEstateAddressSearchList] res: ", res);
    commit(
      SherlockConstant.SET_SHERLOCK_ESTATE_ADDRESS_SEARCH_LIST,
      res?.addressSearchQueries || []
    );
    return res;
  },

  /**************************************************************
   * 단일 주소지 조회 결과
   **************************************************************/
  async acGetSherlockEstateAddressSearchResult(
    { commit },
    { sherlock_address_search_query_seq }
  ) {
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_ESTATE_ADDRESS_SEARCH_RESULT}`,
      params: {
        sherlock_address_search_query_seq,
      },
    });
    // window.console.log("[acGetSherlockEstateAddressSearchResult] res: ", res);
    return res;
  },

  /**************************************************************
   * 주소지 조회 포인트 사용
   * unique_no, // 주소지 고유번호
   **************************************************************/
  async acPostSherlockDealProcessAddressSearch({ commit }, { unique_no }) {
    const res = await callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_DEAL_PROCESS_ADDRESS_SEARCH}`,
      data: { unique_no },
    });
    // window.console.log("[acPostSherlockDealProcessAddressSearch] res: ", res);
    return res;
  },

  /**************************************************************
   * 주소지 조회 포인트 사용
   * sherlock_address_search_owner_seqs, // 소유자 번호
   **************************************************************/
  async acPostSherlockDealProcessOwnerPropertiesFromSearch(
    { commit },
    { sherlock_address_search_owner_seqs }
  ) {
    const res = await callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_DEAL_PROCESS_OWNER_PROPERTIES_FROM_SEARCH}`,
      data: { sherlock_address_search_owner_seqs },
    });
    // window.console.log("[acPostSherlockDealProcessAddressSearch] res: ", res);
    return res;
  },
};

export const getters = {};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
